import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, PanelLeftClose, PanelLeft, LogOut, Search, Settings, ShieldAlert, ShieldCheck, Sparkles } from 'lucide-react';
import { useAuth } from '@/app/AuthProvider';
import { ROLE_LABELS, requiresMfa } from '@/lib/permissions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/Sheet';
import { SidebarContent } from './Sidebar';
import { NotificationBell } from './NotificationBell';

interface TopbarProps {
  onOpenPalette: () => void;
  onOpenAssistant: () => void;
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

export function Topbar({ onOpenPalette, onOpenAssistant, sidebarCollapsed, onToggleSidebar }: TopbarProps) {
  const { profile, mfa, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mfaRequired = requiresMfa(profile?.role ?? null);
  const mfaSatisfied = mfa.currentLevel === 'aal2';
  const mfaIncomplete = mfaRequired && !mfaSatisfied;

  // Cmd/Ctrl+K opens the command palette stub.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenPalette();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenPalette]);

  const initials = (profile?.full_name || profile?.email || 'U')
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-background px-4 sm:px-6">
      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="flex w-64 flex-col p-0 text-sidebar-foreground bg-sidebar border-r-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <SidebarContent onNavigate={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar Toggle */}
      {onToggleSidebar && (
        <Button
          variant="ghost"
          size="icon"
          className="hidden md:flex -ml-2 text-muted-foreground"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
        >
          {sidebarCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </Button>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={onOpenPalette}
        className="gap-2 text-muted-foreground"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="ml-2 hidden rounded border border-border bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
          ⌘ K
        </kbd>
      </Button>

      <div className="ml-auto flex items-center gap-3">
        {/* The AI entry point gets the Atmosphere treatment so it reads as a
            distinct, slightly special affordance next to the neutral chrome. */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenAssistant}
          className="gap-2 rounded-full border border-info-soft/70 bg-info-soft/25 text-info hover:bg-info-soft/45 hover:text-info"
          aria-label="Open assistant"
        >
          <Sparkles className="h-4 w-4" strokeWidth={1.8} />
          <span className="hidden sm:inline">Ask Atlas</span>
          <kbd className="ml-1 hidden rounded border border-info-soft/70 bg-card/70 px-1.5 py-0.5 text-[10px] font-medium text-info sm:inline-flex">
            ⌘ J
          </kbd>
        </Button>
        <NotificationBell />

        {profile && (
          <Badge variant="info-soft" className="hidden md:inline-flex">
            {ROLE_LABELS[profile.role]}
          </Badge>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full p-0.5 ring-2 ring-transparent transition-all hover:ring-info-soft/80 data-[state=open]:ring-info-soft">
              <Avatar className="h-8 w-8">
                {profile?.avatar_url && (
                  <AvatarImage src={profile.avatar_url} alt={profile.email} />
                )}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {profile?.full_name ?? profile?.email}
                </span>
                <span className="text-xs text-muted-foreground">{profile?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/account/security" className="flex items-center gap-2">
                {mfaIncomplete ? (
                  <ShieldAlert className="h-4 w-4 text-destructive" />
                ) : mfaSatisfied ? (
                  <ShieldCheck className="h-4 w-4 text-old-growth" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                <span className="flex-1">Security</span>
                {mfaIncomplete && (
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-destructive">
                    Action needed
                  </span>
                )}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/account/settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" /> Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => signOut()} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
